import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaHome, FaRedo, FaBolt } from 'react-icons/fa';
import toast from 'react-hot-toast';
import Navbar from '../components/Layout/Navbar';
import Timer from '../components/UI/Timer';
import QuestionCard from '../components/Quiz/QuestionCard';
import ScorePopup from '../components/Quiz/ScorePopup';
import Button from '../components/UI/Button';
import api from '../api/axios';
import useTimer from '../hooks/useTimer';
import useSound from '../hooks/useSound';
import { useAuth } from '../context/AuthContext';
import LevelUpModal from '../components/UI/LevelUpModal';
import useLevelUp from '../hooks/useLevelUp';
import PageTransition from '../components/UI/PageTransition';

const TOTAL_QUESTIONS = 20;
const TIME_PER_QUESTION = 20;
const SPEED_BONUS_THRESHOLD = 10; // Bonus si réponse < 10s

function shuffleOptions(question) {
  const options = [...(question.options || question.reponses || [])];
  const correctIdx = question.reponseCorrecte ?? question.correctAnswer ?? 0;
  const correctText = options[correctIdx];

  for (let i = options.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [options[i], options[j]] = [options[j], options[i]];
  }

  return { ...question, options, reponseCorrecte: options.indexOf(correctText) };
}

function ChronoMode() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { checkLevelUp, levelUpData, clearLevelUp } = useLevelUp();
  const { playCorrect, playIncorrect, playTick, playVictory } = useSound();

  // États
  const [questions, setQuestions] = useState([]);
  const [gameId, setGameId] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [score, setScore] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [showScorePopup, setShowScorePopup] = useState(false);
  const [lastPoints, setLastPoints] = useState(0);
  const [error, setError] = useState(null);

  const nextQuestionRef = useRef(null);

  const handleTimeUp = useCallback(() => {
    if (!revealed) {
      playIncorrect();
      setRevealed(true);
      setSelectedAnswer(null);
      setResults((r) => [...r, { correct: false, points: 0, timeBonus: false }]);
    }
  }, [revealed, playIncorrect]);

  const { timeLeft, start, pause, reset } = useTimer(TIME_PER_QUESTION, handleTimeUp);

  // Son de tick à 5 secondes
  useEffect(() => {
    if (timeLeft <= 5 && timeLeft > 0 && !revealed) {
      playTick();
    }
  }, [timeLeft, revealed, playTick]);

  // Chargement des questions
  useEffect(() => {
    setLoading(true);
    api.get(`/api/questions/random?limit=${TOTAL_QUESTIONS}`)
      .then(({ data }) => {
        const rawQ = data.questions || [];
        if (rawQ.length === 0) {
          setError('no_questions');
          setLoading(false);
          return;
        }
        setQuestions(rawQ.map(shuffleOptions));
        setLoading(false);
        // Créer une partie
        api.post('/api/games', { mode: 'chrono', totalQuestions: TOTAL_QUESTIONS })
          .then(({ data: gameData }) => {
            setGameId(gameData.gameId || null);
          })
          .catch(() => {});
      })
      .catch(() => {
        setError('server');
        setLoading(false);
      });
  }, []);

  // Auto-start du timer dès qu'on a les questions
  useEffect(() => {
    if (!loading && questions.length > 0 && !gameOver && !revealed) {
      start();
    }
  }, [loading, questions, gameOver, revealed]);

  const currentQuestion = questions[currentIndex];

  const handleAnswer = useCallback((selectedIdx) => {
    if (revealed || selectedIdx === null) return;

    pause();

    const isCorrect = selectedIdx === currentQuestion.reponseCorrecte;
    let points = 0;
    let hasSpeedBonus = false;

    if (isCorrect) {
      playCorrect();
      // Points de base
      points = 100;
      // Bonus rapidité : +50 points si répondu en moins de 10s
      if (timeLeft > SPEED_BONUS_THRESHOLD) {
        points += 50;
        hasSpeedBonus = true;
      }
      setTotalPoints((p) => p + points);
      setScore((s) => s + 1);
    } else {
      playIncorrect();
      points = 0;
    }

    setSelectedAnswer(selectedIdx);
    setRevealed(true);
    setShowScorePopup(true);
    setLastPoints(points);
    setResults((r) => [...r, { correct: isCorrect, points, timeBonus: hasSpeedBonus }]);

    // Auto-continue après 2s
    nextQuestionRef.current = setTimeout(() => {
      setShowScorePopup(false);
      if (currentIndex < questions.length - 1) {
        setCurrentIndex((i) => i + 1);
        setSelectedAnswer(null);
        setRevealed(false);
        reset();
      } else {
        endGame();
      }
    }, 2000);
  }, [currentQuestion, revealed, timeLeft, currentIndex, questions.length, playCorrect, playIncorrect, pause, reset]);

  const handleContinue = useCallback(() => {
    setShowScorePopup(false);
    if (currentIndex < questions.length - 1) {
      setCurrentIndex((i) => i + 1);
      setSelectedAnswer(null);
      setRevealed(false);
      reset();
    } else {
      endGame();
    }
  }, [currentIndex, questions.length, reset]);

  const endGame = async () => {
    setGameOver(true);
    playVictory();

    const xpGain = Math.floor(totalPoints / 10);
    const coinsGain = Math.floor(totalPoints / 20);

    try {
      if (gameId) {
        await api.post(`/api/games/${gameId}/finish`, {
          score,
          totalQuestions: TOTAL_QUESTIONS,
          totalPoints,
          results,
          mode: 'chrono',
        });
      }

      // MAJ utilisateur
      const leveledUp = checkLevelUp(user?.xp || 0, xpGain);
      updateUser({
        xp: (user?.xp || 0) + xpGain,
        coins: (user?.coins || 0) + coinsGain,
      });

      if (leveledUp) {
        // Affiche modal levelup après 1s
        setTimeout(() => {}, 1000);
      }
    } catch (err) {
      console.error('Erreur fin de partie :', err);
    }
  };

  const handlePlayAgain = () => {
    window.location.reload();
  };

  const handleHome = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center min-h-screen" style={{ background: '#0d2b1a' }}>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            className="w-16 h-16 rounded-full border-4 border-t-transparent"
            style={{ borderColor: '#f0b429', borderTopColor: 'transparent' }}
          />
        </div>
      </PageTransition>
    );
  }

  if (error) {
    return (
      <PageTransition>
        <div className="flex flex-col items-center justify-center min-h-screen px-4" style={{ background: '#0d2b1a' }}>
          <h2 className="text-white text-2xl font-bold mb-4">Erreur</h2>
          <p className="text-white/60 mb-6">Impossible de charger les questions</p>
          <Button onClick={handleHome}>Retour à l'accueil</Button>
        </div>
      </PageTransition>
    );
  }

  if (gameOver) {
    const percentage = Math.round((score / TOTAL_QUESTIONS) * 100);
    const speedBonusCount = results.filter(r => r.timeBonus).length;

    return (
      <PageTransition>
        <div className="relative min-h-screen flex flex-col items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #0d2b1a 0%, #1a4a2e 100%)' }}>
          <Navbar title="CHRONO - RÉSULTATS" showBack onBack={handleHome} />

          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            className="w-full max-w-md"
          >
            {/* Score principal */}
            <div className="text-center mb-6 mt-6">
              <motion.h1
                className="text-6xl font-black mb-2"
                style={{ color: '#f0b429', fontFamily: "'Rajdhani', sans-serif" }}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                {score}/{TOTAL_QUESTIONS}
              </motion.h1>
              <p className="text-white/60 text-sm uppercase tracking-widest" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                Réussite : {percentage}%
              </p>
            </div>

            {/* Statistiques */}
            <motion.div
              className="space-y-3 mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <div className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'rgba(240,180,41,0.1)', border: '1px solid rgba(240,180,41,0.3)' }}>
                <span className="text-white/70">Points totaux</span>
                <span className="text-xl font-bold" style={{ color: '#f0b429', fontFamily: "'Rajdhani', sans-serif" }}>
                  +{totalPoints}
                </span>
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)' }}>
                <span className="text-white/70">Bonus rapidité</span>
                <span className="text-xl font-bold text-green-400" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  x{speedBonusCount}
                </span>
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)' }}>
                <span className="text-white/70">XP gagnée</span>
                <span className="text-xl font-bold text-blue-400" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  +{Math.floor(totalPoints / 10)}
                </span>
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.3)' }}>
                <span className="text-white/70">Coins</span>
                <span className="text-xl font-bold text-purple-400" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  +{Math.floor(totalPoints / 20)}
                </span>
              </div>
            </motion.div>

            {/* Boutons */}
            <motion.div
              className="space-y-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <button
                onClick={handlePlayAgain}
                className="w-full py-4 rounded-xl font-bold text-lg uppercase tracking-wider flex items-center justify-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #22c55e 0%, #15803d 100%)',
                  color: 'white',
                  boxShadow: '0 4px 15px rgba(34,197,94,0.4)',
                }}
              >
                <FaRedo size={16} />
                REJOUER
              </button>

              <button
                onClick={handleHome}
                className="w-full py-3 rounded-xl font-bold text-white uppercase tracking-wider"
                style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                <FaHome size={14} className="inline mr-2" />
                ACCUEIL
              </button>
            </motion.div>
          </motion.div>

          {levelUpData && (
            <LevelUpModal data={levelUpData} onClose={clearLevelUp} />
          )}
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="relative min-h-screen flex flex-col" style={{ background: 'linear-gradient(135deg, #0d2b1a 0%, #1a4a2e 100%)' }}>
        {/* Navbar */}
        <Navbar
          title={`CHRONO ${currentIndex + 1}/${TOTAL_QUESTIONS}`}
          showBack
          onBack={handleHome}
        />

        {/* Progression visuelle */}
        <div className="flex items-center justify-between px-5 py-2">
          <div className="flex-1 h-1 rounded-full mr-3" style={{ background: 'rgba(240,180,41,0.2)' }}>
            <motion.div
              className="h-full rounded-full"
              style={{ background: '#f0b429' }}
              initial={{ width: 0 }}
              animate={{ width: `${((currentIndex + 1) / TOTAL_QUESTIONS) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <span className="text-white/60 text-sm font-bold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            {currentIndex + 1}/{TOTAL_QUESTIONS}
          </span>
        </div>

        {/* Zone question */}
        <div className="flex-1 flex flex-col items-center justify-center px-5 pb-20">
          <AnimatePresence mode="wait">
            {currentQuestion && (
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="w-full"
              >
                {/* Timer circulaire */}
                <div className="flex justify-center mb-6">
                  <motion.div
                    className="flex items-center justify-center w-24 h-24 rounded-full border-4"
                    style={{
                      borderColor: timeLeft <= 5 ? '#ef4444' : '#f0b429',
                      background: 'rgba(0,0,0,0.3)',
                    }}
                    animate={{
                      scale: timeLeft <= 5 ? [1, 1.05, 1] : 1,
                    }}
                    transition={{
                      duration: 0.5,
                      repeat: timeLeft <= 5 ? Infinity : 0,
                    }}
                  >
                    <div className="text-center">
                      <span
                        className="text-3xl font-black"
                        style={{
                          color: timeLeft <= 5 ? '#ef4444' : '#f0b429',
                          fontFamily: "'Rajdhani', sans-serif",
                        }}
                      >
                        {timeLeft}
                      </span>
                      <p className="text-xs text-white/50 uppercase">sec</p>
                    </div>
                  </motion.div>
                </div>

                {/* Question */}
                <QuestionCard
                  question={currentQuestion}
                  selectedAnswer={selectedAnswer}
                  revealed={revealed}
                  onAnswer={handleAnswer}
                  correctAnswerIdx={currentQuestion.reponseCorrecte}
                />

                {/* Popup score */}
                {showScorePopup && (
                  <ScorePopup
                    points={lastPoints}
                    isCorrect={selectedAnswer === currentQuestion.reponseCorrecte}
                    onClose={handleContinue}
                  />
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {levelUpData && (
          <LevelUpModal data={levelUpData} onClose={clearLevelUp} />
        )}
      </div>
    </PageTransition>
  );
}

export default ChronoMode;
